import React, { createContext, ReactNode, useMemo, useState, useEffect } from "react";

import i18next, { i18n as I18nInstance, Resource } from "i18next";
import { initReactI18next } from "react-i18next";

// ▼ 共通翻訳JSONの読み込み
import commonJa from "./locales/ja.json";
import commonEn from "./locales/en.json";

export type TranslationConfig = {
  resources?: Resource;
  defaultLanguage?: "ja" | "en";
};

export type LanguageContextValue = {
  language: string;
  setLanguage: (lang: "ja" | "en") => void;
  t: (key: string, options?: any) => string;
};

export const LanguageContext = createContext<LanguageContextValue | null>(null);

function createI18n(mergedResources: Resource, defaultLanguage: "ja" | "en"): I18nInstance {
  const instance = i18next.createInstance();

  void instance.use(initReactI18next).init({
    resources: mergedResources,
    lng: defaultLanguage,
    fallbackLng: "ja",
    defaultNS: "translation",
    compatibilityJSON: "v4", 
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

  return instance;
}

export function createTranslation(config: TranslationConfig = {}) {
  const defaultLanguage = config.defaultLanguage ?? "ja";

  const extraJa = (config.resources?.ja?.translation || {}) as any;
  const extraEn = (config.resources?.en?.translation || {}) as any;

  const mergedResources: Resource = {
    ja: {
      translation: {
        ...(commonJa as any),
        ...extraJa,
      },
    },
    en: {
      translation: {
        ...(commonEn as any),
        ...extraEn,
      },
    },
  };

  const instance = createI18n(mergedResources, defaultLanguage);

  function TranslationProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<"ja" | "en">(defaultLanguage);

    useEffect(() => {
      instance.changeLanguage(language);
    }, [language]);

    const value = useMemo<LanguageContextValue>(() => {
      return {
        language,
        setLanguage: (lang: "ja" | "en") => setLanguageState(lang),
        t: (key: string, options?: any) => {
          const msg = instance.t(key, { lng: language, ...options });
          if (typeof msg === "string" && msg.length > 0) return msg;
          return key;
        },
      };
    }, [language]);

    return (
      <LanguageContext.Provider value={value}>
        {children}
      </LanguageContext.Provider>
    );
  }

  return { TranslationProvider, LanguageContext, i18next: instance };
}